import { Editor, mergeAttributes, Node } from '@tiptap/core'
import { Node as ProseMirrorNode } from '@tiptap/pm/model'

export type BaseHubBlockOptions = {
  HTMLAttributes: Record<string, string>
  renderLabel: (props: {
    options: BaseHubBlockOptions
    node: ProseMirrorNode
  }) => string
  callbackOnPaste: (
    editor: Editor,
    id: string,
    q?: number
  ) => string | undefined | void | false | null
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    basehubBlock: {
      /**
       * Add a basehub block
       */
      setBaseHubBlock: (options: {
        id: string
        HTMLAttributes?: Record<string, string>
      }) => ReturnType
    }
  }
}

export const BaseHubBlock = Node.create<BaseHubBlockOptions>({
  name: 'basehub-block',
  group: 'block',
  draggable: true,
  selectable: true,
  atom: true,

  addOptions() {
    return {
      HTMLAttributes: {},
      renderLabel: () => '',
      callbackOnPaste: (_editor: Editor, _id: string) => null,
    }
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => {
          return element.getAttribute('id')
        },
        renderHTML: (attributes) => {
          if (!attributes.id) return {}

          return { id: attributes.id }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: `div[data-type="${this.name}"]`,
        getAttrs: (element) => {
          if (typeof element === 'string') {
            return {}
          }
          return element.id ? { id: element.id } : {}
        },
      },
    ]
  },

  renderHTML({ HTMLAttributes, node }) {
    return [
      'div',
      mergeAttributes(
        { 'data-type': this.name, id: node.attrs.id },
        HTMLAttributes
      ),
    ]
  },

  renderText({ node }) {
    return this.options.renderLabel({ options: this.options, node })
  },

  addCommands() {
    return {
      setBaseHubBlock:
        (options) =>
        ({ dispatch, chain, state }) => {
          const { id, HTMLAttributes } = options
          const node = this.editor.state.schema.nodes[this.name]

          if (!node) return false

          if (dispatch) {
            const selection = state.selection
            const previousNode = selection.$head.nodeBefore
            if (
              previousNode &&
              previousNode.isText &&
              previousNode.text?.startsWith('/')
            ) {
              const startPos = selection.$head.pos - previousNode.text!.length
              const endPos = selection.$head.pos!

              chain()
                .deleteRange({
                  from: startPos,
                  to: endPos,
                }) // Delete the slash "/" and the previous text // Split the block at startPos to create a new paragraph
                .insertContentAt(startPos, {
                  type: this.name,
                  attrs: {
                    id,
                    HTMLAttributes,
                  },
                })
            } else {
              // here delete the word starting with / and then insert the block
              chain()
                .deleteRange({
                  from:
                    selection.$head.pos -
                    (selection.$head.node().text?.indexOf('/') ?? 1),
                  to: selection.$head.pos,
                })
                .insertContentAt(selection.head, {
                  type: this.name,
                  attrs: {
                    id,
                    HTMLAttributes,
                  },
                })
            }
          }

          return true
        },
    }
  },
  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isBaseHubBlock = false
          const { selection } = state
          const { empty, anchor } = selection

          if (!empty) return false

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isBaseHubBlock = true
              tr.insertText('', pos, pos + node.nodeSize)

              return false
            }
          })

          return isBaseHubBlock
        }),
    }
  },
})
